import Repository from './Repository';
import { Shop } from './shopRepository';

const resource = '/coupon';

export default {
  /**
   * 交換可能なクーポン一覧
   */
  get(): Promise<{ coupons: Coupon[] }> {
    return Repository.get(`${resource}`);
  },

  /**
   * 購入可能なクーポン一覧
   */
  getPurchaseCoupon(): Promise<{ coupons: Coupon[] }> {
    return Repository.get(`${resource}/purchase_coupon`);
  },

  /**
   * ポイント -> クーポン交換
   */
  exchange(couponId: string) {
    return Repository.post(`${resource}/exchange`, { couponId });
  },

  /**
   * クーポン購入
   */
  purchase(couponId: string) {
    return Repository.post(`${resource}/purchase`, { couponId });
  },

  /**
   * クーポン詳細
   * @param couponId
   * @param [providerType]
   */
  getDetail(couponId: string, providerType: string | undefined = undefined) {
    return Repository.get(`${resource}/${couponId}` + (providerType ? `?providerType=${providerType}` : ``));
  },

  /**
   * 対象店舗一覧
   * @param couponIds
   */
  getAvailableShops(couponIds: string | string[], from: number, limit: number): Promise<{ shops: Shop[] }> {
    const ids = Array.isArray(couponIds) ? couponIds : [couponIds];
    const params = new URLSearchParams();
    ids.forEach(id => params.append('id', id));
    return Repository.get(`${resource}/shop?${params.toString()}`, { params: { from, limit } });
  },

  /**
   * 顧客クーポン一覧
   */
  getCustomerCoupon(): Promise<{ customerCoupons: CustomerCoupon[] }> {
    return Repository.get(`${resource}/customer_coupon`);
  },

  /**
   * 顧客クーポン詳細
   * @param customerCouponId
   */
  getCustomerCouponDetail(customerCouponId: string): Promise<CustomerCoupon> {
    return Repository.get(`${resource}/customer_coupon/${customerCouponId}`);
  },

  /**
   * 顧客クーポン使用
   * @param customerCouponId
   * @param shopId
   */
  useCustomerCoupon(customerCouponId: string, shopId?: string) {
    return Repository.put(`${resource}/customer_coupon/${customerCouponId}/use`, { shopId });
  },

  /**
   * 顧客クーポン使用（複数枚）
   * @param customerCouponIds
   * @param shopId
   */
  bulkUseCustomerCoupon(customerCouponIds: string[], shopId?: string) {
    return Repository.put(`${resource}/customer_coupon/bulk/use`, { customerCouponIds, shopId });
  },

  /**
   * 顧客に対し制限されているクーポンか検証
   * @param coupon
   */
  async checkRestrictedCoupon(coupon: object) {
    return await Repository.post(`${resource}/restriction`, { coupon });
  },

  /**
   * クーポンアクションを実行
   * @memo 2022/05/19 アクションと呼んでいるが、実際はCouponProviderを介した付与処理を呼んでいる
   * @param actionId
   * @param token
   * @param lat CS-8234
   * @param lng CS-8234
   */
  action(
    actionId: string,
    token: string,
    lat: number | null = null,
    lng: number | null = null
  ): Promise<{
    customerCoupon: CustomerCoupon;
    line: {
      link: string;
      displayName: string;
      pictureUrl?: string;
    };
  }> {
    return Repository.post(`${resource}/provider_link/${actionId}`, { token, lat, lng });
  },

  // CS-6461
  /**
   * 抽選済み結果取得
   * @param providerId
   * @param token
   * @param [drawId]
   */
  async getInstantwinResult(providerId: string, token: string, drawId?: string) {
    return Repository.get(
      `${resource}/provider_link/${providerId}/instantwin?token=${token}` + (drawId ? `&drawId=${drawId}` : '')
    );
  },
  /**
   * 抽選を行う
   * @param providerId
   * @param token
   */
  async drawInstantwin(providerId: string, token: string) {
    return Repository.post(`${resource}/provider_link/${providerId}/instantwin?token=${token}`);
  },
  /**
   * 抽選ポストバックを実行する
   * @param providerId
   * @param [drawId]
   */
  async triggerWithInstantwinResult(providerId: string, drawId?: string) {
    return Repository.post(
      `${resource}/provider_link/${providerId}/instantwin/trigger` + (drawId ? `?drawId=${drawId}` : '')
    );
  },

  /**
   * 指定したIDのクーポンプロバイダーを取得
   * @param providerId
   */
  async getCouponProviderById(providerId: string) {
    return Repository.get(`${resource}/coupon_provider/${providerId}`);
  },
};

export type Coupon = {
  id: string;
  couponMaster: {
    name: string;
    point: number;
    attrs: string;
  };
  stock: number | null;
  restrictionSettings?: string;
  publishTermSettings?: string;
  purchaseCouponSettings?: string;
  // CS-9351 クーポン対象店舗のIDリスト
  shopIds?: string[] | null;
  /* JSON
                  {
                    customer?: {
                      type: string;
                      value?: any;
                    }
                  }
                  */
};

export type CustomerCoupon = {
  id: string;
  channelId: string | null;
  couponMaster: {
    name: string;
    point: number;
    attrs: string;
    orgId: string;
    channelId: string | null;
  };
  couponMasterId: string;
  used: boolean;
  disabled: boolean;
  expiresAt: string;
  customerId: string;
  couponId: string;
  updatedAt: string;
  contents?: string;
  // etc...
  coupon: Coupon;
};
